// Feature: M001-F075 — Web 2SV bypass surface (M001-D020 §7.1)
//
// This page used to decide, on its own, whether the override system was on: it
// called GET /v1/admin/2sv-overrides?active=true and, on failure, printed the raw
// server error string into a red box. When the system was disabled that box read
// "2SV override system is disabled" — while the nav banner, which inferred the
// same fact from a different endpoint, claimed something else.
//
// The page's STATE CLAIM now comes from the shared status (the same value the
// banner renders). The list request is demoted to what it always actually was:
// the detail rows behind that claim. If the detail request fails, the page says
// so without changing — or contradicting — the state claim.

import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../layout/Layout';
import AuthContext from '../../context/AuthContext';
import { OVERRIDE_STATE, REFRESH_EVENT, useTwoSvOverrideStatus } from '../../context/TwoSvOverrideStatusContext';
import { twoSvOverrideApi, userApi, organisationApi } from '../../services/api';
import './Admin.css';

// Wording for the non-enabled states. Kept alongside the banner's labels so the
// two surfaces describe the same state in the same terms.
const STATE_NOTICES = {
  [OVERRIDE_STATE.DISABLED]: {
    icon: '🔒',
    heading: '2SV Bypass Disabled',
    detail: 'The 2SV override system is disabled on this backend, so no bypass can be in effect. Overrides cannot be created, listed or applied until it is enabled.',
  },
  [OVERRIDE_STATE.PERMISSION]: {
    icon: '⚠️',
    heading: '2SV Bypass Status Unavailable - Permission Denied',
    detail: 'Your account is not permitted to read the 2SV override system status, so whether a bypass is active could not be determined.',
  },
  [OVERRIDE_STATE.UNAVAILABLE]: {
    icon: '⚠️',
    heading: '2SV Bypass Status Unavailable - Backend Unreachable',
    detail: 'The 2SV override system status could not be determined. Treat the bypass state as unknown until this resolves.',
  },
};

const SCOPE_LABELS = {
  global: { icon: 'All', label: 'Global', description: 'Applies to all users across all organisations' },
  organisation: { icon: 'Org', label: 'Organisation', description: 'Scoped to a specific organisation' },
  user: { icon: 'User', label: 'User', description: 'Scoped to a specific user' },
};

const getOverrideList = (payload) => {
  const data = payload?.data?.data ?? payload?.data ?? payload;
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.overrides)) return data.overrides;
  if (Array.isArray(data?.items)) return data.items;
  return [];
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return String(dateStr);
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatRelativeExpiry = (dateStr) => {
  if (!dateStr) return { text: '—', urgent: false };
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return { text: String(dateStr), urgent: false };
  const now = new Date();
  const diffMs = date.getTime() - now.getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  const diffHours = Math.floor(diffMs / 3_600_000);
  const diffDays = Math.floor(diffMs / 86_400_000);

  if (diffMs <= 0) return { text: 'Expired', urgent: false };
  if (diffMin < 60) return { text: `${diffMin}m remaining`, urgent: diffMin <= 30 };
  if (diffHours < 24) return { text: `${diffHours}h ${diffMin % 60}m remaining`, urgent: diffHours < 2 };
  return { text: `${diffDays}d ${diffHours % 24}h remaining`, urgent: false };
};

const TwoSvOverridesPage = () => {
  const { user: currentUser } = useContext(AuthContext);
  const { state, activeCount } = useTwoSvOverrideStatus();
  const [overrides, setOverrides] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detailError, setDetailError] = useState(false);
  const [revokingId, setRevokingId] = useState(null);
  const [revokeError, setRevokeError] = useState(null);
  const [resolveMap, setResolveMap] = useState({});
  const [resolveLoading, setResolveLoading] = useState(false);

  const isSuperAdmin = currentUser?.role === 'super_admin';
  const systemEnabled = state === OVERRIDE_STATE.ENABLED;

  const fetchOverrides = useCallback(async () => {
    try {
      setLoading(true);
      setDetailError(false);
      const response = await twoSvOverrideApi.listActive();
      setOverrides(getOverrideList(response));
    } catch (err) {
      // Deliberately NOT surfaced as the page's state claim, and deliberately not
      // echoed verbatim: this page no longer tells the operator what the system's
      // state is by quoting a server error string. That is what made the nav bar
      // and this page disagree. The authoritative claim above stands; this only
      // reports that the detail rows behind it could not be loaded.
      console.error('Failed to fetch 2SV override details:', err);
      setOverrides([]);
      setDetailError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  const resolveNames = useCallback(async (list) => {
    if (list.length === 0) return;

    const orgIds = new Set();
    const userIds = new Set();

    list.forEach((o) => {
      if (o.scope === 'organisation' && o.target_id) orgIds.add(Number(o.target_id));
      if (o.scope === 'user' && o.target_id) userIds.add(Number(o.target_id));
      if (o.created_by) userIds.add(Number(o.created_by));
    });

    if (orgIds.size === 0 && userIds.size === 0) return;

    setResolveLoading(true);
    const map = {};

    try {
      if (userIds.size > 0) {
        try {
          const userResponse = await userApi.getUsers({ limit: 1000 });
          const users = userResponse.data?.data?.users || [];
          users.forEach((u) => {
            const uid = String(u.id || u.uuid || '');
            if (uid) {
              map[`user:${uid}`] = u.displayName || u.email || uid;
            }
          });
        } catch (_) {
          userIds.forEach((id) => {
            map[`user:${id}`] = `User #${id}`;
          });
        }
      }

      if (orgIds.size > 0) {
        try {
          const orgResponse = await organisationApi.getMine();
          const orgs = orgResponse.data?.data || [];
          orgs.forEach((org) => {
            const oid = String(org.id);
            if (oid) {
              map[`org:${oid}`] = org.name || oid;
            }
          });
        } catch (_) {
          orgIds.forEach((id) => {
            map[`org:${id}`] = `Organisation #${id}`;
          });
        }
      }

      setResolveMap(map);
    } finally {
      setResolveLoading(false);
    }
  }, []);

  useEffect(() => {
    // Only ask for detail rows when the shared status says there is a system to
    // ask. When it is off, the list endpoint 403s by design (D020) and its error
    // text is exactly the prose this page must stop reading state from.
    if (!systemEnabled) {
      setOverrides([]);
      setDetailError(false);
      setLoading(false);
      return;
    }
    fetchOverrides().then(() => {});
  }, [fetchOverrides, systemEnabled, activeCount]);

  useEffect(() => {
    if (overrides.length > 0) {
      resolveNames(overrides);
    }
  }, [overrides, resolveNames]);

  const handleRevoke = async (override) => {
    const scopeInfo = SCOPE_LABELS[override.scope] || { label: override.scope };
    const target = resolveMap[`${override.scope === 'organisation' ? 'org' : 'user'}:${override.target_id}`] || `#${override.target_id}`;
    const subject = override.scope === 'global' ? 'ALL users' : target;

    const confirmed = window.confirm(
      `Revoke the "${scopeInfo.label}" 2SV bypass affecting ${subject}?\n\nThis will require 2SV step-up for affected users immediately.`,
    );
    if (!confirmed) return;

    setRevokingId(override.id);
    setRevokeError(null);

    try {
      await twoSvOverrideApi.revoke(override.id);
      await fetchOverrides();
      // Re-read the authoritative status so the count every surface shows moves
      // together. Both surfaces read the one value, so both move or neither does.
      window.dispatchEvent(new CustomEvent(REFRESH_EVENT));
    } catch (err) {
      console.error('Failed to revoke 2SV override:', err);
      setRevokeError(
        err.response?.data?.message
        || err.response?.data?.error
        || 'Failed to revoke this override',
      );
    } finally {
      setRevokingId(null);
    }
  };

  const getScopeTarget = (override) => {
    if (override.scope === 'global') return 'All users';
    if (override.scope === 'organisation') {
      return resolveMap[`org:${override.target_id}`] || `Organisation #${override.target_id}`;
    }
    if (override.scope === 'user') {
      return resolveMap[`user:${override.target_id}`] || `User #${override.target_id}`;
    }
    return `#${override.target_id}`;
  };

  const getCreatedBy = (override) => {
    return resolveMap[`user:${override.created_by}`] || `#${override.created_by}`;
  };

  // `state === null` means the shared status has not resolved yet. It must NOT
  // fall through to "No Active Overrides": claiming there is no bypass before
  // anyone has asked is the fail-open version of this very bug.
  const statusPending = state === null || state === undefined;
  const notice = systemEnabled || statusPending ? null : STATE_NOTICES[state];

  return (
    <Layout>
      <div className="admin-settings" data-testid="two-sv-overrides-page" data-override-state={state || 'unknown'}>
        <header className="page-header">
          <div>
            <h1>
              <Link to="/app/admin/settings" className="breadcrumb-link">Admin</Link>
              {' / 2SV Bypass Overrides'}
            </h1>
            <p className="subtitle">Active 2SV bypass overrides that skip step-up verification</p>
          </div>
        </header>

        {revokeError && (
          <div className="settings-message error" data-testid="revoke-error" role="alert">
            {revokeError}
          </div>
        )}

        {notice ? (
          // The system is not enabled (or its state is unknown). This is the
          // authoritative claim, from the same source the nav banner renders.
          <div className="empty-state" data-testid="two-sv-override-state-notice">
            <span className="empty-icon">{notice.icon}</span>
            <h2>{notice.heading}</h2>
            <p>{notice.detail}</p>
            <Link to="/app/admin/settings" className="btn btn-outline">
              ← Back to Settings
            </Link>
          </div>
        ) : statusPending || loading ? (
          <div className="loading-state" data-testid="two-sv-override-loading">
            <div className="spinner" />
            <p>Loading active overrides…</p>
          </div>
        ) : activeCount === 0 ? (
          <div className="empty-state" data-testid="no-active-overrides">
            <span className="empty-icon">✅</span>
            <h2>No Active Overrides</h2>
            <p>All 2SV step-up verification requirements are enforced. No bypasses are currently active.</p>
            <Link to="/app/admin/settings" className="btn btn-outline">
              ← Back to Settings
            </Link>
          </div>
        ) : (
          <div className="overrides-list" data-testid="active-overrides-list">
            <div className="overrides-list-header">
              {/* The count comes from the shared status, NOT from the length of
                  the detail list. If the detail request fails or lags, this page
                  still reports the same number as the nav banner. */}
              <span className="overrides-count" data-testid="overrides-count">
                {activeCount} active {activeCount === 1 ? 'override' : 'overrides'}
                {resolveLoading && ' — resolving names…'}
              </span>
            </div>

            {detailError && (
              <div className="settings-message error" data-testid="override-detail-error" role="alert">
                Override details could not be loaded. The count above is authoritative.
              </div>
            )}

            <div className="override-cards">
              {overrides.map((override) => {
                const scopeInfo = SCOPE_LABELS[override.scope] || { icon: '❓', label: override.scope };
                const expiry = formatRelativeExpiry(override.expires_at);

                return (
                  <div
                    key={override.id}
                    className={`override-card override-card--${override.scope}`}
                    data-testid={`override-card-${override.id}`}
                  >
                    <div className="override-card-header">
                      <span className="override-scope-icon">{scopeInfo.icon}</span>
                      <span className="override-scope-label">{scopeInfo.label}</span>
                      <span className="override-id">#{override.id}</span>
                    </div>

                    <div className="override-card-body">
                      <div className="override-detail">
                        <span className="override-detail-label">Scope</span>
                        <span className="override-detail-value" data-testid={`override-scope-${override.id}`}>
                          {scopeInfo.icon} {scopeInfo.label}
                        </span>
                      </div>

                      <div className="override-detail">
                        <span className="override-detail-label">Applied to</span>
                        <span className="override-detail-value" data-testid={`override-target-${override.id}`}>
                          {getScopeTarget(override)}
                        </span>
                      </div>

                      {override.reason && (
                        <div className="override-detail">
                          <span className="override-detail-label">Reason</span>
                          <span className="override-detail-value override-reason">{override.reason}</span>
                        </div>
                      )}

                      <div className="override-detail">
                        <span className="override-detail-label">Created by</span>
                        <span className="override-detail-value">{getCreatedBy(override)}</span>
                      </div>

                      <div className="override-detail">
                        <span className="override-detail-label">Created</span>
                        <span className="override-detail-value">{formatDateTime(override.created_at)}</span>
                      </div>

                      <div className="override-detail">
                        <span className="override-detail-label">Expires</span>
                        <span
                          className={`override-detail-value ${expiry.urgent ? 'override-expiry-urgent' : ''}`}
                          data-testid={`override-expiry-${override.id}`}
                        >
                          {formatDateTime(override.expires_at)}{' '}
                          <span className="override-relative-expiry">({expiry.text})</span>
                        </span>
                      </div>
                    </div>

                    <div className="override-card-actions">
                      {isSuperAdmin ? (
                        <button
                          className="btn btn-danger btn-sm"
                          disabled={revokingId === override.id}
                          onClick={() => handleRevoke(override)}
                          data-testid={`revoke-override-${override.id}`}
                        >
                          {revokingId === override.id ? 'Revoking…' : 'Revoke Override'}
                        </button>
                      ) : (
                        <span className="override-permission-note" data-testid="override-permission-note">
                          Super admin required to revoke
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default TwoSvOverridesPage;
